import React, { useState } from 'react';
import { convertSpreadsheetToRanking, convertRankingToJSX, RankingData } from '../utils/spreadsheetConverter';

const SpreadsheetConverter: React.FC = () => {
  const [tsvData, setTsvData] = useState('');
  const [rankingTitle, setRankingTitle] = useState('');
  const [rankingDescription, setRankingDescription] = useState('');
  const [source, setSource] = useState('');
  const [convertedJSX, setConvertedJSX] = useState('');
  const [convertedHTML, setConvertedHTML] = useState('');
  const [previewData, setPreviewData] = useState<RankingData | null>(null);

  const handleConvert = () => {
    if (!tsvData.trim() || !rankingTitle.trim()) {
      alert('データとタイトルを入力してください');
      return;
    }

    try {
      const rankingData = convertSpreadsheetToRanking(
        tsvData,
        rankingTitle,
        rankingDescription,
        source
      );

      const jsx = convertRankingToJSX(rankingData);
      const html = convertRankingToJSX(rankingData);

      setConvertedJSX(jsx);
      setConvertedHTML(html);
      setPreviewData(rankingData);
    } catch (error) {
      alert('データの変換中にエラーが発生しました: ' + error);
    }
  };

  const handleCopyJSX = () => {
    navigator.clipboard.writeText(convertedJSX);
    alert('JSXコードをクリップボードにコピーしました！');
  };

  const handleCopyHTML = () => {
    navigator.clipboard.writeText(convertedHTML);
    alert('HTMLコードをクリップボードにコピーしました！');
  };

  const handleClear = () => {
    setTsvData('');
    setRankingTitle('');
    setRankingDescription('');
    setSource('');
    setConvertedJSX('');
    setConvertedHTML('');
    setPreviewData(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            📊 スプレッドシート → ランキング変換ツール
          </h1>
          <p className="text-lg text-gray-600">
            スプレッドシートのデータをコピーして、美しいランキング表示に変換できます
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 入力セクション */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                1. データ入力
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ランキングタイトル *
                  </label>
                  <input
                    type="text"
                    value={rankingTitle}
                    onChange={(e) => setRankingTitle(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="例: 2024年高校サッカー 得点王ランキング"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    説明文
                  </label>
                  <textarea
                    value={rankingDescription}
                    onChange={(e) => setRankingDescription(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ランキングの説明を入力してください"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    出典
                  </label>
                  <input
                    type="text"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="例: 日本サッカー協会"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    スプレッドシートデータ (TSV形式) *
                  </label>
                  <textarea
                    value={tsvData}
                    onChange={(e) => setTsvData(e.target.value)}
                    rows={10}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder={`1\t田中太郎\t15得点\tFW\t/image1.jpg
2\t佐藤次郎\t12得点\tMF\t/image2.jpg
3\t鈴木三郎\t10得点\tDF\t/image3.jpg`}
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    形式: 順位 [Tab] 名前 [Tab] 値 [Tab] 説明 [Tab] 画像URL
                  </p>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={handleConvert}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    変換する
                  </button>
                  <button
                    onClick={handleClear}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    クリア
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 出力セクション */}
          <div className="space-y-6">
            {/* プレビュー */}
            {previewData && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  2. プレビュー
                </h2>
                <div 
                  className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: convertedHTML }}
                />
              </div>
            )}

            {/* JSX出力 */}
            {convertedJSX && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    3. JSXコード
                  </h2>
                  <button
                    onClick={handleCopyJSX}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                  >
                    コピー
                  </button>
                </div>
                <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                  <code>{convertedJSX}</code>
                </pre>
              </div>
            )}

            {/* HTML出力 */}
            {convertedHTML && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    4. HTMLコード
                  </h2>
                  <button
                    onClick={handleCopyHTML}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                  >
                    コピー
                  </button>
                </div>
                <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                  <code>{convertedHTML}</code>
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* 使用例 */}
        <div className="mt-12 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            📋 使用方法
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">1. スプレッドシートの準備</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                <li>スプレッドシートでランキングデータを作成</li>
                <li>列の順序: 順位 | 名前 | 値 | 説明 | 画像URL</li>
                <li>ヘッダー行は除外してデータのみ選択</li>
                <li>Ctrl+C (Mac: Cmd+C) でコピー</li>
              </ol>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">2. 変換と使用</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                <li>コピーしたデータを「スプレッドシートデータ」に貼り付け</li>
                <li>タイトルと説明を入力</li>
                <li>「変換する」ボタンをクリック</li>
                <li>生成されたJSXコードを記事にコピー</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpreadsheetConverter;
